// apps/provider/src/services/api/video.js
// Telehealth video session methods
//
// Methods: createVideoSession, getVideoSession, getVideoSessionByAppointment,
//          startVideoSession, endVideoSession, updateVideoSession,
//          refreshVideoTokens, joinVideoSessionPublic, updatePatientJoinStatusPublic
//
// Response types: @prism-ehr/shared → VideoSession, PublicVideoSessionInfo, PublicVideoSessionUpdateResult

import { authService } from '../auth';

/**
 * @returns {Object} Video domain methods (bound to `this` of the composed apiService)
 */
export function createVideoMethods() {
  return {
    /**
     * Create a new video session for an appointment.
     *
     * Xano: POST /video_session
     * Xano file: 804_video_session_POST.xs
     *
     * @param {string} appointmentId
     * @returns {Promise<import('@prism-ehr/shared').VideoSession>}
     */
    createVideoSession(appointmentId) {
      return this.request('/video_session', {
        method: 'POST',
        body: JSON.stringify({
          appointment_id: appointmentId,
          provider_id: authService.getUserProfile().id,
          status: 'created',
          created_at: Date.now(),
          scheduled_start: Date.now()
        })
      }, 'createVideoSession');
    },

    /**
     * Get a video session by ID.
     *
     * Xano: GET /video_session/{id}
     * Xano file: 802_video_session_video_session_id_GET.xs
     *
     * @param {string} sessionId
     * @returns {Promise<import('@prism-ehr/shared').VideoSession>}
     */
    getVideoSession(sessionId) {
      return this.request(`/video_session/${sessionId}`, {
        method: 'GET'
      }, 'getVideoSession');
    },

    /**
     * Get a video session by appointment ID.
     *
     * Xano: GET /video_session
     * Xano file: 803_video_session_GET.xs
     *
     * @param {string} appointmentId
     * @returns {Promise<import('@prism-ehr/shared').VideoSession[]>}
     */
    getVideoSessionByAppointment(appointmentId) {
      return this.request(`/video_session?appointment_id=${appointmentId}`, {
        method: 'GET'
      }, 'getVideoSessionByAppointment');
    },

    /**
     * Start (activate) a video session.
     *
     * Xano: PATCH /video_session/{id}
     * Xano file: 805_video_session_video_session_id_PATCH.xs
     *
     * @param {string} sessionId
     * @returns {Promise<import('@prism-ehr/shared').VideoSession>}
     */
    startVideoSession(sessionId) {
      return this.request(`/video_session/${sessionId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'active',
          actual_start: Date.now(),
          provider_joined_at: Date.now()
        })
      }, 'startVideoSession');
    },

    /**
     * End (complete) a video session.
     *
     * Xano: PATCH /video_session/{id}
     * Xano file: 805_video_session_video_session_id_PATCH.xs
     *
     * @param {string} sessionId
     * @returns {Promise<import('@prism-ehr/shared').VideoSession>}
     */
    endVideoSession(sessionId) {
      const now = Date.now();
      return this.request(`/video_session/${sessionId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'completed',
          actual_end: now,
          provider_left_at: now
        })
      }, 'endVideoSession');
    },

    /**
     * Update a video session with arbitrary data.
     *
     * Xano: PATCH /video_session/{id}
     * Xano file: 805_video_session_video_session_id_PATCH.xs
     *
     * @param {string} sessionId
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').VideoSession>}
     */
    updateVideoSession(sessionId, data) {
      return this.request(`/video_session/${sessionId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }, 'updateVideoSession');
    },

    /**
     * Refresh JWT tokens for an existing video session.
     *
     * Xano: POST /video-session-refresh
     * Xano file: video-session-refresh_POST.xs
     *
     * @param {string} videoSessionId
     * @returns {Promise<import('@prism-ehr/shared').VideoSession>}
     */
    refreshVideoTokens(videoSessionId) {
      return this.request('/video-session-refresh', {
        method: 'POST',
        body: JSON.stringify({
          video_session_id: videoSessionId
        })
      }, 'refreshVideoTokens');
    },

    /**
     * Join a video session via public token (patient-facing).
     *
     * Xano: GET /public/video-session
     * Xano file: 1005_public_video_session_GET.xs
     *
     * @param {string} token
     * @returns {Promise<import('@prism-ehr/shared').PublicVideoSessionInfo>}
     */
    joinVideoSessionPublic(token) {
      return this.publicRequest(`/public/video-session?token=${token}`, {
        method: 'GET'
      });
    },

    /**
     * Update patient join status for a video session (public).
     *
     * Xano: PATCH /public/video-session
     * Xano file: 1006_public_video_session_PATCH.xs
     *
     * @param {string} token
     * @returns {Promise<import('@prism-ehr/shared').PublicVideoSessionUpdateResult>}
     */
    updatePatientJoinStatusPublic(token) {
      return this.publicRequest(`/public/video-session`, {
        method: 'PATCH',
        body: JSON.stringify({
          token: token,
          patient_joined_at: Date.now(),
          status: 'active'
        })
      });
    },
  };
}
